import { CalendarDays } from 'lucide-react';
import { Select } from '@/components/ui';

export const DATE_PRESETS = ['all', '7d', '30d', '90d'] as const;
export type DatePreset = (typeof DATE_PRESETS)[number];

const PRESET_LABELS: Record<DatePreset, string> = {
  all: 'All time',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
};

const DATE_OPTIONS = DATE_PRESETS.map((preset) => ({ value: preset, label: PRESET_LABELS[preset] }));

/** Human label for a preset ("Last 30 days"), echoed to the bulk-SMS recipient rail as a filter chip. */
export function datePresetLabel(preset: DatePreset): string {
  return PRESET_LABELS[preset];
}

export function DateRangeSelect({
  value,
  onChange,
  labelPrefix,
  compact = false,
}: {
  value: DatePreset;
  onChange: (next: DatePreset) => void;
  /**
   * Says what the range applies to when nothing around the select does (e.g. in a tab toolbar). `Select` names the
   * trigger "<prefix> <range>" (its aria-label plus the value), so the range on screen stays in its name
   * (voice control, WCAG label in name).
   */
  labelPrefix?: string;
  /** Content-width pill with a calendar icon in front (a page's top row) instead of the full-width filter. */
  compact?: boolean;
}) {
  const select = (
    <Select
      variant="filter"
      aria-label={labelPrefix}
      active={value !== 'all'}
      options={DATE_OPTIONS}
      value={value}
      onChange={(next) => onChange(next as DatePreset)}
      placeholder="All time"
      {...(compact ? { className: 'w-auto gap-2 pl-11 font-semibold', containerClassName: 'w-auto' } : {})}
    />
  );
  if (!compact) return select;
  return (
    <div className="relative inline-block">
      {select}
      <CalendarDays aria-hidden="true" className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-clovie-muted" />
    </div>
  );
}
